/* What a disagreement on this definition is worth telling somebody, edited in place.
 *
 * **The editors are always there for somebody allowed to use them**, as the design draws the panel:
 * a checkbox, a severity and a threshold under one line saying what the policy does today, and a
 * Save. Somebody who is not allowed sees the line and a role-gated chip naming the grant.
 *
 * **A threshold below 1 is refused rather than saved.** "Open an incident when a run finds at least
 * zero breaks" is not a quieter setting — it opens an incident for a run that found nothing.
 */

import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { BreakPolicy, BreakSeverity } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { CONFIGURE_RECONCILIATION_PERMISSION } from '@/entities/session';

import { useBreakPolicy, useSaveBreakPolicy } from '../api/governance';

const SEVERITIES: readonly BreakSeverity[] = ['info', 'warning', 'critical'];

export function BreakPolicyPanel({
  definitionId,
  canConfigure,
}: {
  readonly definitionId: string;
  readonly canConfigure: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const policy = useBreakPolicy(definitionId);

  return (
    <section className="lq-card lq-rc__panel lq-rdf__panel">
      <HelpLabel
        subject={t('reconciliation.policy.title')}
        help={t('help.term.breakPolicy')}
      >
        <h3 className="lq-rc__panelTitle">{t('reconciliation.policy.title')}</h3>
      </HelpLabel>
      {policy.isPending && <SkeletonRows rows={2} columns={1} />}
      {policy.isError && (
        <Alert tone="error" title={t('reconciliation.policy.failed')}>
          {policy.error.message}
        </Alert>
      )}
      {policy.isSuccess && (
        <PolicyBody
          /* Keyed on what is stored, so a save that lands re-seeds the editors from the server's
             answer rather than leaving the draft that produced it. */
          key={`${String(policy.data.enabled)}:${policy.data.severity}:${String(policy.data.min_breaks)}`}
          definitionId={definitionId}
          policy={policy.data}
          canConfigure={canConfigure}
        />
      )}
    </section>
  );
}

function PolicyBody({
  definitionId,
  policy,
  canConfigure,
}: {
  readonly definitionId: string;
  readonly policy: BreakPolicy;
  readonly canConfigure: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveBreakPolicy(definitionId);
  const [enabled, setEnabled] = useState(policy.enabled);
  const [severity, setSeverity] = useState<BreakSeverity>(policy.severity);
  const [minBreaks, setMinBreaks] = useState(String(policy.min_breaks));
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const errorId = useId();

  const line = policy.enabled
    ? t('reconciliation.policy.line.on', {
        severity: policy.severity,
        count: policy.min_breaks,
      })
    : t('reconciliation.policy.line.off');

  return (
    <>
      <p className="lq-rc__panelSub">{line}</p>
      {canConfigure ? (
        <>
          <label className="lq-rdf__check">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => {
                setEnabled(event.target.checked);
                setSaved(false);
              }}
            />
            {t('reconciliation.policy.enabledLabel')}
          </label>
          <div className="lq-rdf__fields">
            <label className="lq-rdf__field">
              {t('reconciliation.policy.severity')}
              <select
                className="lq-input"
                value={severity}
                onChange={(event) => {
                  setSeverity(event.target.value as BreakSeverity);
                  setSaved(false);
                }}
              >
                {SEVERITIES.map((each) => (
                  <option key={each} value={each}>
                    {t(`reconciliation.severity.${each}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="lq-rdf__field">
              {t('reconciliation.policy.atLeast')}
              <input
                className="lq-input lq-rdf__min"
                inputMode="numeric"
                value={minBreaks}
                aria-describedby={problem === null ? undefined : errorId}
                onChange={(event) => {
                  setMinBreaks(event.target.value);
                  setProblem(null);
                  setSaved(false);
                }}
              />
            </label>
          </div>
          {problem !== null && (
            <p id={errorId} role="alert" className="lq-rdf__error">
              {problem}
            </p>
          )}
          {save.isError && (
            <p role="alert" className="lq-rdf__error">
              {save.error.message}
            </p>
          )}
          {saved && (
            <p role="status" className="lq-rdf__saved">
              {t('reconciliation.policy.saved')}
            </p>
          )}
          <Button
            size="compact"
            className="lq-rdf__start"
            busy={save.isPending}
            onClick={() => {
              const value = Number(minBreaks.trim());
              if (minBreaks.trim() === '' || !Number.isInteger(value) || value < 1) {
                setProblem(t('reconciliation.policy.error.min'));
                return;
              }
              save.mutate(
                { enabled, severity, min_breaks: value },
                {
                  onSuccess: () => {
                    setSaved(true);
                  },
                },
              );
            }}
          >
            {t('reconciliation.policy.save')}
          </Button>
        </>
      ) : (
        <span
          className="lq-gated lq-rdf__start"
          title={t('reconciliation.needs', {
            permission: CONFIGURE_RECONCILIATION_PERMISSION,
          })}
        >
          {t('reconciliation.policy.gated')}
        </span>
      )}
    </>
  );
}
